"use client";
import {
  alpha,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Typography,
  useTheme,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  Tooltip,
  Badge,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  FaCheckCircle,
  FaUserAlt,
  FaUserCog,
  FaUserGraduate,
  FaUsers,
  FaUserShield,
  FaUserTie,
  FaWallet,
  FaDollarSign,
  FaCalendarCheck,
  FaClipboardList,
  FaHandHoldingUsd,
  FaMoneyBillWave,
  FaExclamationTriangle,
  FaClock,
  FaChartLine,
  FaAward,
  FaGraduationCap,
  FaSpinner,
  FaRupeeSign,
  FaCoins,
} from "react-icons/fa";
import {
  MdAccountBalance,
  MdTrendingUp,
  MdTrendingDown,
  MdMoreVert,
  MdNotifications,
  MdArrowUpward,
  MdArrowDownward,
} from "react-icons/md";
import {
  Cell,
  Pie,
  PieChart,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  BarChart,
  Bar,
  Area,
  AreaChart,
} from "recharts";
import { motion } from "framer-motion";
import HomeScreenHeader from "./HomeHeader";
import api from "../../api/axiosInstance";
import { ScholarshipApplicationsListingProps } from "../../typings/scholarship-applications";
import { UserListProps } from "../../typings";
import { TransactionListProps } from "../../typings/transactions";
import { AccountListProps } from "../../typings";
import { FlexCol, FlexRow, Spacer } from "../../components";
import { UserRolesEnum } from "../../typings";

interface DashboardMetrics {
  totalUsers: number;
  totalApplications: number;
  totalTransactions: number;
  totalAccounts: number;
  totalAmountGranted: number;
  totalAmountDisbursed: number;
  approvalRate: number;
  pendingApplications: number;
  rejectedApplications: number;
  processedApplications: number;
  recentActivities: ScholarshipApplicationsListingProps[];
}

const MotionCard = motion(Card);
const MotionBox = motion(Box);

// Custom styled components for enhanced aesthetics
const StyledCard = motion(Card);
const GradientCard = ({ children, gradient, ...props }: any) => (
  <StyledCard
    {...props}
    sx={{
      background: gradient,
      borderRadius: 4,
      boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
      border: "1px solid rgba(255,255,255,0.2)",
      backdropFilter: "blur(10px)",
      position: "relative",
      overflow: "hidden",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)",
        pointerEvents: "none",
      },
      ...props.sx,
    }}
  >
    {children}
  </StyledCard>
);

export default function HomeDashboard() {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [users, setUsers] = useState<UserListProps[]>([]);
  const [scholarshipApplications, setScholarshipApplications] = useState<
    ScholarshipApplicationsListingProps[]
  >([]);
  const [transactions, setTransactions] = useState<TransactionListProps[]>([]);
  const [accounts, setAccounts] = useState<AccountListProps[]>([]);
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);

  // Calculate comprehensive statistics from real data
  const calculateMetrics = (): DashboardMetrics => {
    const totalAmountGranted = scholarshipApplications.reduce(
      (sum, app) => sum + (app.amount_granted || 0),
      0
    );

    const totalAmountDisbursed = scholarshipApplications.reduce(
      (sum, app) => sum + (app.amount_paid_till_now || 0),
      0
    );

    const approvedApplications = scholarshipApplications.filter(
      (app) => app.status === "Approved"
    );

    const paidApplications = scholarshipApplications.filter(
      (app) => app.status === "Paid"
    );

    const pendingApplications = scholarshipApplications.filter(
      (app) => app.status === "In Process" || app.status === "Submitted"
    );

    const rejectedApplications = scholarshipApplications.filter(
      (app) => app.status === "Rejected"
    );

    // Calculate processed applications (approved + rejected + paid)
    const processedApplications =
      approvedApplications.length + rejectedApplications.length + paidApplications.length;
    
    // Calculate approval rate based on processed applications only (approved + paid vs rejected)
    const approvalRate =
      processedApplications > 0
        ? ((approvedApplications.length + paidApplications.length) / processedApplications) * 100
        : 0;

    // Get recent activities (last 10 applications sorted by ID)
    const recentActivities = [...scholarshipApplications]
      .sort((a, b) => (b.id || 0) - (a.id || 0))
      .slice(0, 10);

    return {
      totalUsers: users.length,
      totalApplications: scholarshipApplications.length,
      totalTransactions: transactions.length,
      totalAccounts: accounts.length,
      totalAmountGranted,
      totalAmountDisbursed,
      approvalRate,
      pendingApplications: pendingApplications.length,
      rejectedApplications: rejectedApplications.length,
      processedApplications,
      recentActivities,
    };
  };

  // User statistics
  const userStats = {
    students:    users.filter((user) => user.role === UserRolesEnum.STUDENT).length,
    accountants: users.filter((user) => user.role === UserRolesEnum.ACCOUNTANT).length,
    donors:      users.filter((user) => user.role === UserRolesEnum.DONOR).length,
    admins:      users.filter(
      (user) => user.role === UserRolesEnum.ADMIN || user.role === UserRolesEnum.SUPER_ADMIN
    ).length,
    interviewers: users.filter((user) => user.role === UserRolesEnum.INTERVIEWER).length,
    mentors:      users.filter((user) => user.role === UserRolesEnum.MENTOR).length,
    total: users.length,
  };

  // Application statistics - Updated to include "Paid" status
  const applicationStats = {
    submitted: scholarshipApplications.filter(
      (app) => app.status === "Submitted"
    ).length,
    approved: scholarshipApplications.filter((app) => app.status === "Approved")
      .length,
    paid: scholarshipApplications.filter((app) => app.status === "Paid")
      .length,
    rejected: scholarshipApplications.filter((app) => app.status === "Rejected")
      .length,
    inProcess: scholarshipApplications.filter(
      (app) => app.status === "In Process"
    ).length,
    total: scholarshipApplications.length,
  };

  // Enhanced status distribution with paid status - Updated to include "Paid" status
  const statusDistribution = [
    {
      name: "Approved",
      value: applicationStats.approved,
      color: "#10B981",
      gradient: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
    },
    {
      name: "Paid",
      value: applicationStats.paid,
      color: "#6938EF",
      gradient: "linear-gradient(135deg, #6938EF 0%, #5925DC 100%)",
    },
    {
      name: "Submitted",
      value: applicationStats.submitted,
      color: "#3B82F6",
      gradient: "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)",
    },
    {
      name: "Rejected",
      value: applicationStats.rejected,
      color: "#EF4444",
      gradient: "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
    },
    {
      name: "In Process",
      value: applicationStats.inProcess,
      color: "#F59E0B",
      gradient: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
    },
  ].filter((item) => item.value > 0);

  // Enhanced user distribution with gradients
  const userDistribution = [
    {
      name: "Students",
      value: userStats.students,
      color: "#8B5CF6",
      gradient: "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)",
    },
    {
      name: "Donors",
      value: userStats.donors,
      color: "#10B981",
      gradient: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
    },
    {
      name: "Admins",
      value: userStats.admins,
      color: "#F59E0B",
      gradient: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
    },
    {
      name: "Interviewers",
      value: userStats.interviewers,
      color: "#EC4899",
      gradient: "linear-gradient(135deg, #EC4899 0%, #DB2777 100%)",
    },
    {
      name: "Mentors",
      value: userStats.mentors,
      color: "#06B6D4",
      gradient: "linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)",
    },
    {
      name: "Accountants",
      value: userStats.accountants,
      color: "#84CC16",
      gradient: "linear-gradient(135deg, #84CC16 0%, #65A30D 100%)",
    },
  ].filter((item) => item.value > 0);

  const fetchData = async () => {
    try {
      setRefreshing(true);
      const [usersRes, applicationsRes, transactionsRes, accountsRes] =
        await Promise.all([
          api.get("/users"),
          api.get("/scholarship-applications"),
          api.get("/accounts/transactions/list/"),
          api.get("/accounts"),
        ]);

      setUsers(usersRes.data.data || []);
      setScholarshipApplications(applicationsRes.data.data || []);
      setTransactions(transactionsRes.data.data || []);
      setAccounts(accountsRes.data.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!loading && users.length >= 0) {
      setMetrics(calculateMetrics());
    }
  }, [users, scholarshipApplications, transactions, accounts, loading]);

  const handleRefresh = () => {
    fetchData();
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100%" }}>
      <HomeScreenHeader onRefresh={handleRefresh} refreshing={refreshing} />

      <Box sx={{ flexGrow: 1, p: { xs: 2, sm: 3 } }}>
        {/* Key Metrics Cards */}
        <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <GradientCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 }, position: "relative", zIndex: 1 }}>
                <Box sx={{ 
                  display: "flex", 
                  alignItems: "center", 
                  mb: { xs: 2, sm: 3 },
                  flexDirection: { xs: "column", sm: "row" },
                  textAlign: { xs: "center", sm: "left" }
                }}>
                  <Avatar
                    sx={{
                      bgcolor: "rgba(255,255,255,0.2)",
                      color: "white",
                      mr: { xs: 0, sm: 2 },
                      mb: { xs: 1, sm: 0 },
                      width: { xs: 48, sm: 56, md: 64 },
                      height: { xs: 48, sm: 56, md: 64 },
                      boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
                    }}
                  >
                    <FaUsers size={24} />
                  </Avatar>
                  <Box>
                    <Typography 
                      variant="h3" 
                      fontWeight="bold" 
                      color="white"
                      sx={{ fontSize: { xs: "1.5rem", sm: "1.8rem", md: "2rem" } }}
                    >
                      {metrics?.totalUsers || 0}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ 
                        color: "rgba(255,255,255,0.8)",
                        fontSize: { xs: "0.75rem", sm: "0.875rem" }
                      }}
                    >
                      Total Users
                    </Typography>
                  </Box>
                </Box>
                <Typography
                  variant="caption"
                  sx={{ 
                    color: "rgba(255,255,255,0.7)",
                    fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    display: "block",
                    textAlign: { xs: "center", sm: "left" }
                  }}
                >
                  Active system users across all roles
                </Typography>
              </CardContent>
            </GradientCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <GradientCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              gradient="linear-gradient(135deg, #2c3e50 0%, #34495e 100%)"
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 }, position: "relative", zIndex: 1 }}>
                <Box sx={{ 
                  display: "flex", 
                  alignItems: "center", 
                  mb: { xs: 2, sm: 3 },
                  flexDirection: { xs: "column", sm: "row" },
                  textAlign: { xs: "center", sm: "left" }
                }}>
                  <Avatar
                    sx={{
                      bgcolor: "rgba(255,255,255,0.2)",
                      color: "white",
                      mr: { xs: 0, sm: 2 },
                      mb: { xs: 1, sm: 0 },
                      width: { xs: 48, sm: 56, md: 64 },
                      height: { xs: 48, sm: 56, md: 64 },
                      boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
                    }}
                  >
                    <FaClipboardList size={24} />
                  </Avatar>
                  <Box>
                    <Typography 
                      variant="h3" 
                      fontWeight="bold" 
                      color="white"
                      sx={{ fontSize: { xs: "1.5rem", sm: "1.8rem", md: "2rem" } }}
                    >
                      {metrics?.totalApplications || 0}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ 
                        color: "rgba(255,255,255,0.8)",
                        fontSize: { xs: "0.75rem", sm: "0.875rem" }
                      }}
                    >
                      Applications
                    </Typography>
                  </Box>
                </Box>
                <Typography
                  variant="caption"
                  sx={{ 
                    color: "rgba(255,255,255,0.7)",
                    fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    display: "block",
                    textAlign: { xs: "center", sm: "left" }
                  }}
                >
                  {metrics && metrics.totalApplications === 0
                    ? "No applications yet"
                    : metrics && metrics.processedApplications > 0
                    ? `${metrics.processedApplications} processed (${metrics.approvalRate.toFixed(1)}% approval rate)`
                    : `${metrics?.processedApplications || 0} applications processed`}
                </Typography>
              </CardContent>
            </GradientCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <GradientCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              gradient="linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)"
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 }, position: "relative", zIndex: 1 }}>
                <Box sx={{ 
                  display: "flex", 
                  alignItems: "center", 
                  mb: { xs: 2, sm: 3 },
                  flexDirection: { xs: "column", sm: "row" },
                  textAlign: { xs: "center", sm: "left" }
                }}>
                  <Avatar
                    sx={{
                      bgcolor: "rgba(255,255,255,0.2)",
                      color: "white",
                      mr: { xs: 0, sm: 2 },
                      mb: { xs: 1, sm: 0 },
                      width: { xs: 48, sm: 56, md: 64 },
                      height: { xs: 48, sm: 56, md: 64 },
                      boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
                    }}
                  >
                    <FaCoins size={24} />
                  </Avatar>
                  <Box>
                    <Typography 
                      variant="h3" 
                      fontWeight="bold" 
                      color="white"
                      sx={{ fontSize: { xs: "1.5rem", sm: "1.8rem", md: "2rem" } }}
                    >
                      {(metrics?.totalAmountGranted || 0).toLocaleString()}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ 
                        color: "rgba(255,255,255,0.8)",
                        fontSize: { xs: "0.75rem", sm: "0.875rem" }
                      }}
                    >
                      Amount Granted
                    </Typography>
                  </Box>
                </Box>
                <Typography
                  variant="caption"
                  sx={{ 
                    color: "rgba(255,255,255,0.7)",
                    fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    display: "block",
                    textAlign: { xs: "center", sm: "left" }
                  }}
                >
                  Total scholarship funding approved
                </Typography>
              </CardContent>
            </GradientCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <GradientCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              gradient="linear-gradient(135deg, #10b981 0%, #059669 100%)"
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 }, position: "relative", zIndex: 1 }}>
                <Box sx={{ 
                  display: "flex", 
                  alignItems: "center", 
                  mb: { xs: 2, sm: 3 },
                  flexDirection: { xs: "column", sm: "row" },
                  textAlign: { xs: "center", sm: "left" }
                }}>
                  <Avatar
                    sx={{
                      bgcolor: "rgba(255,255,255,0.2)",
                      color: "white",
                      mr: { xs: 0, sm: 2 },
                      mb: { xs: 1, sm: 0 },
                      width: { xs: 48, sm: 56, md: 64 },
                      height: { xs: 48, sm: 56, md: 64 },
                      boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
                    }}
                  >
                    <MdAccountBalance size={24} />
                  </Avatar>
                  <Box>
                    <Typography 
                      variant="h3" 
                      fontWeight="bold" 
                      color="white"
                      sx={{ fontSize: { xs: "1.5rem", sm: "1.8rem", md: "2rem" } }}
                    >
                      {metrics?.totalAccounts || 0}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ 
                        color: "rgba(255,255,255,0.8)",
                        fontSize: { xs: "0.75rem", sm: "0.875rem" }
                      }}
                    >
                      Active Accounts
                    </Typography>
                  </Box>
                </Box>
                <Typography
                  variant="caption"
                  sx={{ 
                    color: "rgba(255,255,255,0.7)",
                    fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    display: "block",
                    textAlign: { xs: "center", sm: "left" }
                  }}
                >
                  {metrics?.totalTransactions || 0} total transactions recorded
                </Typography>
              </CardContent>
            </GradientCard>
          </Grid>
        </Grid>

        {/* Charts and Analytics */}
        <Grid container spacing={{ xs: 2, md: 4 }}>
          {/* Application Status Distribution */}
          <Grid item xs={12} md={6}>
            <MotionCard
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
              sx={{
                borderRadius: 4,
                boxShadow: "0 12px 40px rgba(0,0,0,0.1)",
                background: "#ffffff",
                border: "1px solid rgba(0,0,0,0.05)",
                height: 480,
              }}
            >
              <CardHeader
                sx={{ pb: 1 }}
                title={
                  <FlexRow align="center">
                    <Box>
                      <Avatar
                        sx={{ bgcolor: "primary.main", width: 40, height: 40 }}
                      >
                        <FaChartLine size={20} />
                      </Avatar>
                    </Box>
                    <Spacer horizontal={1} />
                    <FlexCol>
                      <Typography variant="h6" fontWeight="bold">
                        Application Status Distribution
                      </Typography>
                      <Typography fontSize={12} sx={{ color: "#6b7280" }}>
                        Overview of application statuses
                      </Typography>
                    </FlexCol>
                  </FlexRow>
                }
              />
              <Divider />
              <CardContent sx={{ height: 360 }}>
                {statusDistribution.length > 0 ? (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        height: 280,
                      }}
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={statusDistribution}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                            label={({ name, percent }) =>
                              percent > 0.05 ? `${name} ${(percent * 100).toFixed(0)}%` : ''
                            }
                          >
                            {statusDistribution.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <RechartsTooltip
                            contentStyle={{
                              backgroundColor: "white",
                              borderRadius: "16px",
                              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                              border: "none",
                              padding: "12px 16px",
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1.5,
                        mt: 2,
                        justifyContent: "center",
                      }}
                    >
                      {statusDistribution.map((status) => (
                        <Chip
                          key={status.name}
                          label={`${status.name}: ${status.value}`}
                          size="medium"
                          sx={{
                            background: status.gradient,
                            color: "white",
                            fontWeight: "600",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                          }}
                        />
                      ))}
                    </Box>
                  </>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: 320,
                    }}
                  >
                    <Avatar
                      sx={{ bgcolor: "grey.100", width: 80, height: 80, mb: 2 }}
                    >
                      <FaClipboardList
                        size={40}
                        color={theme.palette.text.disabled}
                      />
                    </Avatar>
                    <Typography variant="h6" sx={{ mb: 1, color: "#6b7280" }}>
                      {metrics && metrics.totalApplications === 0
                        ? "No Applications Yet"
                        : "No Application Status Data"}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.disabled"
                      textAlign="center"
                    >
                      {metrics && metrics.totalApplications === 0
                        ? "Application status data will appear here once applications are submitted"
                        : "Status distribution will appear here once applications are processed"}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </MotionCard>
          </Grid>

          {/* User Role Distribution */}
          <Grid item xs={12} md={6}>
            <MotionCard
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
              sx={{
                borderRadius: 4,
                boxShadow: "0 12px 40px rgba(0,0,0,0.1)",
                background: "#ffffff",
                border: "1px solid rgba(0,0,0,0.05)",
                height: 480,
              }}
            >
              <CardHeader
                sx={{ pb: 1 }}
                title={
                  <FlexRow align="center">
                    <Box>
                      <Avatar
                        sx={{
                          bgcolor: "secondary.main",
                          width: 40,
                          height: 40,
                        }}
                      >
                        <FaUsers size={20} />
                      </Avatar>
                    </Box>
                    <Spacer horizontal={1} />
                    <FlexCol>
                      <Typography variant="h6" fontWeight="bold">
                        User Role Distribution
                      </Typography>
                      <Typography fontSize={12} sx={{ color: "#6b7280" }}>
                        Breakdown of system users by role
                      </Typography>
                    </FlexCol>
                  </FlexRow>
                }
              />
              <Divider />
              <CardContent sx={{ height: 360 }}>
                {userDistribution.length > 0 ? (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        height: 280,
                      }}
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={userDistribution}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                            label={({ name, percent }) =>
                              percent > 0.05 ? `${name} ${(percent * 100).toFixed(0)}%` : ''
                            }
                          >
                            {userDistribution.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <RechartsTooltip
                            contentStyle={{
                              backgroundColor: "white",
                              borderRadius: "16px",
                              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                              border: "none",
                              padding: "12px 16px",
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1.5,
                        mt: 2,
                        justifyContent: "center",
                      }}
                    >
                      {userDistribution.map((user) => (
                        <Chip
                          key={user.name}
                          label={`${user.name}: ${user.value}`}
                          size="medium"
                          sx={{
                            background: user.gradient,
                            color: "white",
                            fontWeight: "600",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                          }}
                        />
                      ))}
                    </Box>
                  </>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: 320,
                    }}
                  >
                    <Avatar
                      sx={{ bgcolor: "grey.100", width: 80, height: 80, mb: 2 }}
                    >
                      <FaUsers size={40} color={theme.palette.text.disabled} />
                    </Avatar>
                    <Typography variant="h6" sx={{ mb: 1, color: "#6b7280" }}>
                      No Users Yet
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.disabled"
                      textAlign="center"
                    >
                      User distribution data will appear here once users are
                      added
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </MotionCard>
          </Grid>

          {/* Financial Overview */}
          <Grid item xs={12} md={6}>
            <MotionCard
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.7 }}
              sx={{
                borderRadius: 4,
                boxShadow: "0 12px 40px rgba(0,0,0,0.1)",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                position: "relative",
                overflow: "hidden",
                height: 480,
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)",
                  pointerEvents: "none",
                },
              }}
            >
              <CardHeader
                sx={{ pb: 1, position: "relative", zIndex: 1 }}
                title={
                  <FlexRow align="center">
                    <Box>
                      <Avatar
                        sx={{
                          bgcolor: "rgba(255,255,255,0.2)",
                          color: "white",
                          width: 40,
                          height: 40,
                        }}
                      >
                        <FaHandHoldingUsd size={20} />
                      </Avatar>
                    </Box>
                    <Spacer horizontal={1} />
                    <FlexCol>
                      <Typography variant="h6" fontWeight="bold" color="white">
                        Financial Overview
                      </Typography>
                      <Typography
                        fontSize={12}
                        sx={{ color: "rgba(255,255,255,0.8)" }}
                      >
                        Scholarship funding insights
                      </Typography>
                    </FlexCol>
                  </FlexRow>
                }
              />
              <Divider sx={{ backgroundColor: "rgba(255,255,255,0.2)" }} />
              <CardContent sx={{ position: "relative", zIndex: 1 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        textAlign: "center",
                        p: 3,
                        bgcolor: "rgba(255,255,255,0.1)",
                        borderRadius: 3,
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: "#10B981",
                          mx: "auto",
                          mb: 2,
                          width: 64,
                          height: 64,
                          boxShadow: "0 8px 24px rgba(16, 185, 129, 0.3)",
                        }}
                      >
                        <FaMoneyBillWave size={28} />
                      </Avatar>
                      <Typography variant="h4" fontWeight="bold" color="white">
                        PKR{" "}
                        {(metrics?.totalAmountGranted || 0).toLocaleString()}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "rgba(255,255,255,0.8)" }}
                      >
                        Total Amount Granted
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        textAlign: "center",
                        p: 3,
                        bgcolor: "rgba(255,255,255,0.1)",
                        borderRadius: 3,
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: "#F59E0B",
                          mx: "auto",
                          mb: 2,
                          width: 64,
                          height: 64,
                          boxShadow: "0 8px 24px rgba(245, 158, 11, 0.3)",
                        }}
                      >
                        <FaWallet size={28} />
                      </Avatar>
                      <Typography variant="h4" fontWeight="bold" color="white">
                        PKR{" "}
                        {(metrics?.totalAmountDisbursed || 0).toLocaleString()}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "rgba(255,255,255,0.8)" }}
                      >
                        Total Amount Disbursed
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        mt: 2,
                        p: 3,
                        bgcolor: "rgba(255,255,255,0.1)",
                        borderRadius: 3,
                      }}
                    >
                      <Typography
                        variant="body1"
                        color="white"
                        gutterBottom
                        fontWeight="600"
                      >
                        Disbursement Progress
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={
                          metrics?.totalAmountGranted &&
                          metrics.totalAmountGranted > 0
                            ? (metrics.totalAmountDisbursed /
                                metrics.totalAmountGranted) *
                              100
                            : 0
                        }
                        sx={{
                          height: 12,
                          borderRadius: 6,
                          bgcolor: "rgba(255,255,255,0.2)",
                          "& .MuiLinearProgress-bar": {
                            bgcolor: "#10B981",
                            borderRadius: 6,
                            boxShadow: "0 0 10px rgba(16, 185, 129, 0.5)",
                          },
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ color: "rgba(255,255,255,0.8)", mt: 1 }}
                      >
                        {metrics?.totalAmountGranted &&
                        metrics.totalAmountGranted > 0
                          ? `${(
                              (metrics.totalAmountDisbursed /
                                metrics.totalAmountGranted) *
                              100
                            ).toFixed(1)}% disbursed`
                          : "No disbursements yet"}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </MotionCard>
          </Grid>

          {/* Recent Activities */}
          <Grid item xs={12} md={6}>
            <MotionCard
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.8 }}
              sx={{
                borderRadius: 4,
                boxShadow: "0 12px 40px rgba(0,0,0,0.1)",
                background: "#ffffff",
                border: "1px solid rgba(0,0,0,0.05)",
                height: 480,
                display: "flex",
                flexDirection: "column",
                "&:hover": {
                  boxShadow: "0 16px 50px rgba(0,0,0,0.15)",
                  transform: "translateY(-2px)",
                },
                transition: "all 0.3s ease",
              }}
            >
              <CardHeader
                sx={{ pb: 1, flexShrink: 0 }}
                title={
                  <FlexRow align="center">
                    <Box>
                      <Avatar
                        sx={{ bgcolor: "success.main", width: 40, height: 40 }}
                      >
                        <FaClock size={20} />
                      </Avatar>
                    </Box>
                    <Spacer horizontal={1} />
                    <FlexCol>
                      <Typography variant="h6" fontWeight="bold">
                        Recent Activities
                      </Typography>
                      <Typography fontSize={12} sx={{ color: "#6b7280" }}>
                        Latest application updates
                      </Typography>
                    </FlexCol>
                  </FlexRow>
                }
              />
              <Divider sx={{ flexShrink: 0 }} />
              <CardContent
                sx={{
                  p: 1,
                  flexGrow: 1,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {metrics?.recentActivities &&
                metrics.recentActivities.length > 0 ? (
                  <Box sx={{ flexGrow: 1, overflow: "auto" }}>
                    {metrics.recentActivities.slice(0, 6).map((app, index) => (
                      <Box
                        key={app.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          py: 1.5,
                          px: 2,
                          borderBottom:
                            index <
                            Math.min(metrics.recentActivities.length, 6) - 1
                              ? "1px solid rgba(0,0,0,0.08)"
                              : "none",
                          "&:hover": {
                            bgcolor: "rgba(0,0,0,0.02)",
                            borderRadius: 1,
                          },
                          transition: "all 0.2s ease",
                        }}
                      >
                        {/* Status Indicator */}
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor:
                              app.status === "Approved"
                                ? "#10B981"
                                : app.status === "Paid"
                                ? "#6938EF"
                                : app.status === "Rejected"
                                ? "#EF4444"
                                : app.status === "Submitted"
                                ? "#3B82F6"
                                : "#F59E0B",
                            mr: 2,
                            flexShrink: 0,
                          }}
                        />

                        {/* Content */}
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            fontWeight="600"
                            sx={{
                              mb: 0.25,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {app.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              display: "block",
                              fontSize: "0.75rem",
                              color: "#6b7280",
                            }}
                          >
                            Application {app.status.toLowerCase()} • ID:{" "}
                            {app.id}
                          </Typography>
                        </Box>

                        {/* Status Badge */}
                        <Chip
                          label={app.status}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: "0.7rem",
                            fontWeight: "500",
                            bgcolor:
                              app.status === "Approved"
                                ? "rgba(16, 185, 129, 0.1)"
                                : app.status === "Paid"
                                ? "rgba(105, 56, 239, 0.1)"
                                : app.status === "Rejected"
                                ? "rgba(239, 68, 68, 0.1)"
                                : app.status === "Submitted"
                                ? "rgba(59, 130, 246, 0.1)"
                                : "rgba(245, 158, 11, 0.1)",
                            color:
                              app.status === "Approved"
                                ? "#10B981"
                                : app.status === "Paid"
                                ? "#6938EF"
                                : app.status === "Rejected"
                                ? "#EF4444"
                                : app.status === "Submitted"
                                ? "#3B82F6"
                                : "#F59E0B",
                            border: `1px solid ${
                              app.status === "Approved"
                                ? "rgba(16, 185, 129, 0.2)"
                                : app.status === "Paid"
                                ? "rgba(105, 56, 239, 0.2)"
                                : app.status === "Rejected"
                                ? "rgba(239, 68, 68, 0.2)"
                                : app.status === "Submitted"
                                ? "rgba(59, 130, 246, 0.2)"
                                : "rgba(245, 158, 11, 0.2)"
                            }`,
                            "& .MuiChip-label": {
                              px: 1,
                            },
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      flexGrow: 1,
                      p: 4,
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        bgcolor: "grey.100",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 2,
                      }}
                    >
                      <FaClock size={20} color={theme.palette.text.disabled} />
                    </Box>
                    <Typography
                      variant="body1"
                      fontWeight="500"
                      sx={{ mb: 0.5, color: "#6b7280" }}
                    >
                      No Recent Activities
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.disabled"
                      textAlign="center"
                    >
                      Application activities will appear here
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </MotionCard>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
